import { ARTICLE_IMPORT_PATH, articlePicturePath, articleReadPath, articlePicturesPath, isArticleImportRefusal, t, type ArticleImportOutcome, type ArticleImportRefusal, type ImportAnswers, type SuggestionRow, type TagTargetRow } from '@bms2/shared';
import { apiFetch, upload } from '../records/api';

/** 🔨 Relative to the API's own root, never prefixed with /api (piece 1). */
export const KNOWLEDGE_PATH = '/knowledge';
export const KNOWLEDGE_SEARCH_PATH = `${KNOWLEDGE_PATH}/search`;
export const ARTICLES_PATH = `${KNOWLEDGE_PATH}/articles`;
export const KNOWLEDGE_OPTIONS_PATH = `${KNOWLEDGE_PATH}/options`;
export const TAG_TARGETS_PATH = `${KNOWLEDGE_PATH}/tag-targets`;
export const CATEGORIES_PATH = `${KNOWLEDGE_PATH}/categories`;
/** 🔒 Add New Certification writes a Training requirement type here (T307; decision 0871). */
export const KNOWLEDGE_REQUIREMENT_TYPES_PATH = `${KNOWLEDGE_PATH}/requirement-types`;
/** One article, the read the phone shares (decision 9580). */
export const articlePath = (id: string): string => articleReadPath(id);
export const articleActionPath = (id: string, action: 'publish' | 'archive' | 'tags' | 'linkable-files' | 'pdf'): string => `${articlePath(id)}/${action}`;
export const articleTagPath = (id: string, tagId: string): string => `${articlePath(id)}/tags/${tagId}`;
export const categoryPath = (id: string): string => `${CATEGORIES_PATH}/${id}`;
/** 🔒 An article's pictures (decision 0800): where the picture tool uploads, and where one picture is drawn from. */
export const articlePicturesUploadPath = (id: string): string => articlePicturesPath(id);
export const articlePictureImagePath = (id: string, fileId: string): string => articlePicturePath(id, fileId);
export const categoryGrantsPath = (id: string): string => `${categoryPath(id)}/grants`;
/** 🔒 Import a file as a Draft article (T339; decision 0953); the file itself is not kept (decision 0992). */
export const KNOWLEDGE_IMPORT_PATH = ARTICLE_IMPORT_PATH;

/** What the import answers: the new draft or the reader's questions, or the refusal with its reason where the reader named one. */
export type ImportOutcome = { ok: true; data: ArticleImportOutcome } | { ok: false; message: string; refusal: ArticleImportRefusal | null; status: number };

/**
 * The import's upload (T339): multipart, the file and the category, answered
 * with the new draft's id. 🔒 A refusal keeps the reason the reader named, so
 * the window says it in the lookup's own sentence.
 */
export async function importArticleFile(file: File, categoryId: string | null, apiBaseUrl = '', answers: ImportAnswers | null = null): Promise<ImportOutcome> {
  const form = new FormData();
  form.append('file', file);
  if (categoryId) form.append('categoryId', categoryId);
  // 🔒 The first post carries no answers and writes nothing where the reader is unsure; the second carries them (decision 1014).
  if (answers) form.append('answers', JSON.stringify(answers));
  // The reader's questions write nothing, so only a finished import announces a write (T671: the one upload helper does the rest).
  const sent = await upload<ArticleImportOutcome>(KNOWLEDGE_IMPORT_PATH, form, apiBaseUrl, undefined, (data) => data?.status !== 'asking');
  if (sent.ok) return { ok: true, data: sent.data };
  const payload = sent.body as { refusal?: unknown } | null | undefined;
  return { ok: false, status: sent.status ?? 0, refusal: isArticleImportRefusal(payload?.refusal) ? payload.refusal : null, message: sent.message };
}
export const categoryGrantPath = (id: string, grantId: string): string => `${categoryGrantsPath(id)}/${grantId}`;

/** The + Tag Record picker's search: AssetNode and Item together, one row each, keyed by type and id. */
export function tagTargetSearch(apiBaseUrl = ''): (query: string, signal: AbortSignal) => Promise<SuggestionRow[]> {
  return async (query, signal) => {
    const response = await apiFetch(`${apiBaseUrl}${TAG_TARGETS_PATH}?q=${encodeURIComponent(query)}`, { signal });
    if (!response.ok) return [];
    const rows = (await response.json()) as TagTargetRow[];
    return (Array.isArray(rows) ? rows : []).map((row) => ({ id: `${row.recordType}:${row.recordId}`, line1: row.label, line2: row.recordType === 'Item' ? t('kb.tag.item') : t('kb.tag.asset') }));
  };
}

/** The quiz's routes (piece 30), under the article it belongs to. */
export const quizPath = (id: string): string => `${articlePath(id)}/quiz`;
export const quizPreviewPath = (id: string): string => `${quizPath(id)}/preview`;
export const quizSittingPath = (id: string): string => `${quizPath(id)}/sitting`;
export const quizAttemptsPath = (id: string): string => `${quizPath(id)}/attempts`;
export const quizReportPath = (id: string): string => `${quizPath(id)}/report`;
export const questionPath = (questionId: string): string => `${KNOWLEDGE_PATH}/questions/${questionId}`;
export const personReportPath = (personId: string): string => `${KNOWLEDGE_PATH}/people/${personId}/report`;
